import type { ComponentProps } from 'react';
import { useRouter } from 'next/router';
import { clearSavedUserFilter, updateWithToast } from '@/clientOnlyUtils/clientUtils';
import React from 'react';

import { TeamAvatar, type TeamLogo } from '@poseidon/components/Avatar';
import { Button } from '@poseidon/components/Input2';
import { cn } from '@poseidon/components/cn';
import { WaveSpinner } from '@poseidon/components/WaveSpinner';

import { TableCellHeader } from '@poseidon/components/TableCellHeader';
import { useMeUpdateMutation } from '@/hooks/trpc/useUserMutation';
import { Cell2, CellLoading, Row, Table } from '@poseidon/components/Table';
import UserIcons, { type UserIconsNImage } from '~/components/UserIcons';
import type UserTeamStatus from '@poseidon/types/pg-gen/userTeamStatus';

interface TeamsTableProps {
  selectedTeamId?: string;
  teams: TeamRowTeam[];
  isLoading?: boolean;
}
export const TeamsTable = ({ teams, selectedTeamId, isLoading }: TeamsTableProps) => {
  const [isUpdatingId, setIsUpdatingId] = React.useState<string>();
  const meUpdateMutation = useMeUpdateMutation();
  const router = useRouter();

  const onClick = async (defaultTeamId: string, teamName: string) => {
    setIsUpdatingId(defaultTeamId);
    await updateWithToast({
      loading: 'Selecting team...',
      success: `Selected ${teamName}`,
      mutationFnAsync: () => meUpdateMutation.mutateAsync({ data: { defaultTeamId } }),
      onAfterSuccess: () => {
        clearSavedUserFilter();
        localStorage.removeItem('form');
        void router.push(`/teams/active`);
      },
    });
    setIsUpdatingId(undefined);
  };

  return (
    <Table gridTemplateColumns="1fr min-content min-content" bordered>
      <TableCellHeader>Name</TableCellHeader>
      <TableCellHeader />
      <TableCellHeader className="justify-end">Users</TableCellHeader>

      {isLoading && <CellLoading index={1}>Loading Teams</CellLoading>}
      {!isLoading &&
        teams?.map((team) => (
          <TeamRow
            key={team.teamId}
            active={team.teamId == selectedTeamId}
            team={team}
            isUpdatingId={isUpdatingId}
            onClick={() => onClick(team.teamId, team.teamName)}
          />
        ))}
    </Table>
  );
};


export type TeamRowTeam = {
  teamId: string;
  teamPriceTier: "premium" | "standard";
  teamName: string;
  teamLogo: TeamLogo | null;
  userTeamStatus: UserTeamStatus;
  totalUserCount: number,
  nImages: UserIconsNImage[];
};

type TeamRowProps = ComponentProps<'div'> & {
  active?: boolean;
  team: TeamRowTeam;

  isUpdatingId?: string;
};
const TeamRow = ({ team, active, isUpdatingId, onClick }: TeamRowProps) => {
  const isUpdating = isUpdatingId === team?.teamId;
  const isPremium = team.teamPriceTier === 'premium';
  return (
    <Row onClick={onClick} active={active}>
      <Cell2 className="p-6">
        {isUpdating && (
          <div className={cn('m-0 size-8 rounded-full text-haiti-600 dark:bg-slate-900 dark:text-zumthor-600')}>
            <WaveSpinner className="size-full" />
          </div>
        )}

        {!isUpdating && team != null && (
          <TeamAvatar logo={team.teamLogo} name={team.teamName} className="size-8" shape="circle" />
        )}

        <div className="w-1" />
        {team?.teamName}
        <div className="w-1" />
        {active && <Button size="2xs" color="blue" readOnly>Active</Button>}
      </Cell2>
      <Cell2 className="justify-end p-6">
        {isPremium && <Button size="2xs" color="green" tone="outline" readOnly>Premium</Button>}
        {team.userTeamStatus === 'admin' && <Button size="2xs" color="blue" tone="outline" readOnly>Admin</Button>}
        {team.userTeamStatus === 'removed' && <Button size="2xs" color="red" tone="outline" readOnly>Removed</Button>}
      </Cell2>
      <Cell2 className="justify-end p-6">
        <UserIcons totalUserCount={team.totalUserCount} nImages={team.nImages} />
      </Cell2>
    </Row>
  );
};
